"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState, useRef, useId } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, Calendar, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Clock, ShieldCheck, Send, GraduationCap } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormFieldDefinition {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  options?: Array<{
    value: string;
    label: string;
    price?: number;
  }>;
  validation?: {
    messages?: {
      required?: string;
      invalid?: string;
    };
  };
}
interface FormDefinition {
  id: number;
  key: string;
  name: string;
  description?: string;
  availability: {
    accepting: boolean;
    state: string;
    message?: string;
  };
  fields: FormFieldDefinition[];
  submit?: {
    label?: string;
    processing_label?: string;
  };
  spam: {
    token: string;
    min_seconds: number;
    honeypot?: {
      field: string;
    } | null;
  };
}
export default function CallToAction() {
  const shouldReduceMotion = useReducedMotion();
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(0);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationHtml, setConfirmationHtml] = useState<string | null>(null);
  const [honeypotVal, setHoneypotVal] = useState("");
  const honeypotInputId = useId();
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    if (!client || typeof client.pluginRequest !== "function") {
      setIsLoading(false);
      setLoadError("Servicio de formularios temporalmente no disponible.");
      return;
    }
    const fetchDefinition = async () => {
      try {
        setIsLoading(true);
        const data = await client.pluginRequest("wvc-forms/v1/forms/training_info_request_form");
        if (isMounted) {
          setFormDef(data);
          setFetchTimestamp(Date.now());
          setIsLoading(false);
        }
      } catch (err: any) {
        if (err?.status === 404) {
          try {
            const listData = await client.pluginRequest("wvc-forms/v1/forms");
            const items = listData?.items || [];
            const matched = items.find((item: any) => item.key === "training_info_request_form") || items.find((item: any) => item.name?.toLowerCase().includes("capacitación") || item.name?.toLowerCase().includes("training")) || (items.length === 1 ? items[0] : null);
            if (matched && isMounted) {
              const retryData = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
              setFormDef(retryData);
              setFetchTimestamp(Date.now());
              setIsLoading(false);
              return;
            }
          } catch {
            // fallback handling below
          }
        }
        if (isMounted) {
          setIsLoading(false);
          setLoadError("No fue posible cargar el formulario de cotización.");
        }
      }
    };
    fetchDefinition();
    return () => {
      isMounted = false;
    };
  }, []);
  const handleInputChange = (fieldId: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[fieldId];
        return next;
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || isSubmitting) return;
    setGeneralError(null);
    const minSec = formDef.spam?.min_seconds ?? 3;
    const elapsedSec = (Date.now() - fetchTimestamp) / 1000;
    if (elapsedSec < minSec) {
      const waitTimeMs = Math.ceil((minSec - elapsedSec) * 1000);
      await new Promise(resolve => setTimeout(resolve, waitTimeMs));
    }
    setIsSubmitting(true);
    try {
      const client = getWvcClient();
      const payload: Record<string, any> = {
        values: formValues,
        token: formDef.spam.token,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef.spam?.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: honeypotVal
        };
      }
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: payload
      });
      if (res.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
        } else if (res.confirmation?.message) {
          setConfirmationHtml(res.confirmation.message);
        } else {
          setConfirmationHtml("<p>Agradecemos su solicitud. Uno de nuestros consultores senior se pondrá en contacto a la brevedad para coordinar la propuesta institucional.</p>");
        }
      } else if (res.code === "frms_validation_failed") {
        setFieldErrors(res.errors || {});
        setGeneralError(res.message || "Por favor, revise los campos marcados.");
      } else if (res.code === "frms_invalid_token") {
        setGeneralError("La sesión expiró. Recargando parámetros de seguridad...");
        const refreshed = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}`);
        setFormDef(refreshed);
        setFetchTimestamp(Date.now());
      } else {
        setGeneralError(res.message || "No fue posible procesar la solicitud.");
      }
    } catch {
      setGeneralError("Ocurrió un error en la conexión. Intente nuevamente en unos momentos.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const getFieldByHint = (fieldHints: string[]): FormFieldDefinition | undefined => {
    if (!formDef?.fields) return undefined;
    return formDef.fields.find(f => fieldHints.some(hint => f.key.toLowerCase() === hint.toLowerCase() || f.label.toLowerCase().includes(hint.toLowerCase())));
  };
  const nameField = getFieldByHint(["nombre", "name", "full_name", "nombre_completo"]);
  const orgField = getFieldByHint(["entidad", "organizacion", "empresa", "institucion", "dependencia"]);
  const emailField = getFieldByHint(["correo", "email", "email_address", "correo_electronico"]);
  const phoneField = getFieldByHint(["telefono", "phone", "tel", "telefono_contacto"]);
  const programField = getFieldByHint(["programa", "curso", "taller", "programa_interes", "temario"]);
  const messageField = getFieldByHint(["mensaje", "requerimientos", "detalles", "mensaje_requerimientos", "observaciones"]);
  const assignedFieldIds = new Set([nameField, orgField, emailField, phoneField, programField, messageField].filter(Boolean).map(f => f!.id));
  const unassignedFields = (formDef?.fields || []).filter(f => !assignedFieldIds.has(f.id) && !["heading", "paragraph", "total", "payment"].includes(f.type));
  return <section id="llamado-a-la-acción" data-nav="dark" className="dark bg-background text-foreground relative py-20 lg:py-28 overflow-hidden border-t border-border/40">
      <div className="absolute inset-0 pointer-events-none opacity-5">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-chart-2 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-chart-1 blur-3xl" />
      </div>

      <div className="container mx-auto px-6 md:px-10 lg:px-16 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Institutional context */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col justify-between h-full">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-muted/20 border border-chart-2/40 text-xs font-semibold uppercase tracking-widest text-chart-2 mb-6">
                <GraduationCap className="size-3.5" />
                <span>Capacitación Institucional</span>
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-[2.65rem] font-bold uppercase tracking-tight leading-tight mb-4">
                Talleres a la medida de su organización
              </h2>

              <div className="w-12 h-[1.5px] bg-chart-2 mb-6" />

              <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-8">
                Diseñamos programas técnicos presenciales o virtuales orientados a dependencias
                gubernamentales y empresas privadas, alineados con el calendario administrativo y
                requerimientos de cumplimiento de su equipo.
              </p>

              <div className="space-y-4 pt-2 border-t border-border/40 mb-8">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-sm bg-card/60 border border-chart-2/30 text-chart-2 shrink-0 mt-0.5">
                    <Building2 className="size-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground">
                      Sector Público y Privado
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      Impartición en sede institucional, corporativa o mediante plataforma ejecutiva en línea.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-sm bg-card/60 border border-chart-2/30 text-chart-2 shrink-0 mt-0.5">
                    <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground">
                      Adaptación de Cronogramas
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      Sesiones estructuradas sin interrumpir las jornadas operativas de su personal.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-sm bg-card/60 border border-chart-2/30 text-chart-2 shrink-0 mt-0.5">
                    <ShieldCheck className="size-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-foreground">
                      Certificación con Validez Curricular
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      Materiales actualizados conforme al marco normativo vigente, CONAC y disposiciones fiscales.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-sm bg-card/40 border border-border/40 text-xs text-muted-foreground">
              <div className="flex items-center gap-2 text-foreground font-medium mb-1">
                <Clock className="size-3.5 text-chart-2" />
                <span>Tiempo estimado de respuesta</span>
              </div>
              <p>Atención formal de su solicitud de cotización en menos de 24 horas hábiles.</p>
            </div>
          </motion.div>

          {/* Right Column: Inquiry Form */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-7 bg-card/70 border border-border/80 rounded-md p-6 sm:p-8 lg:p-10 shadow-lg relative">
            <div className="mb-6">
              <h3 className="text-xl font-bold uppercase tracking-wider text-foreground">
                Solicitud de cotización de capacitación
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Complete los datos para elaborar una propuesta técnica y económica formal.
              </p>
            </div>

            {isLoading ? <div className="py-16 text-center space-y-3">
                <div className="size-8 border-2 border-chart-2 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-xs text-muted-foreground uppercase tracking-wider">
                  Cargando formulario institucional...
                </p>
              </div> : loadError ? <div className="p-6 rounded-sm bg-destructive/10 border border-destructive/30 text-destructive text-sm flex items-start gap-3">
                <AlertCircle className="size-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">{loadError}</p>
                  <p className="text-xs mt-1 text-muted-foreground">
                    Comuníquese directamente a javilez@avsconsultores.org o al teléfono 667 250 5982.
                  </p>
                </div>
              </div> : confirmationHtml ? <div className="py-8 text-center space-y-4">
                <div className="size-12 rounded-full bg-chart-2/20 border border-chart-2 text-chart-2 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="size-7" />
                </div>
                <h4 className="text-lg font-bold uppercase tracking-wide text-foreground">
                  Solicitud registrada con éxito
                </h4>
                <div className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto" dangerouslySetInnerHTML={{
              __html: confirmationHtml
            }} />
                <Button variant="outline" size="sm" onClick={() => {
              setConfirmationHtml(null);
              setFormValues({});
            }} className="mt-4 border-chart-2/50 text-foreground hover:bg-card">
                  Enviar otra consulta
                </Button>
              </div> : <form onSubmit={handleSubmit} className="space-y-4">
                {formDef?.spam?.honeypot?.field && <div style={{
              position: "absolute",
              left: "-9999px",
              opacity: 0
            }} aria-hidden="true">
                    <label htmlFor={honeypotInputId}>No llenar este campo</label>
                    <input id={honeypotInputId} type="text" name={formDef.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={honeypotVal} onChange={e => setHoneypotVal(e.target.value)} />
                  </div>}

                {generalError && <div className="p-3 rounded-sm bg-destructive/15 border border-destructive/40 text-destructive text-xs flex items-center gap-2">
                    <AlertCircle className="size-4 shrink-0" />
                    <span>{generalError}</span>
                  </div>}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Nombre completo */}
                  {nameField && <div className="space-y-1.5">
                      <Label htmlFor={nameField.id} className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                        {nameField.label || "Nombre completo"}
                        {nameField.required && <span className="text-chart-2 ml-1">*</span>}
                      </Label>
                      <Input id={nameField.id} type="text" placeholder={nameField.placeholder || "Ej. Lic. Carlos Méndez"} value={formValues[nameField.id] || ""} onChange={e => handleInputChange(nameField.id, e.target.value)} className="bg-background/80 border-border/80 text-foreground placeholder:text-muted-foreground/60 h-10 text-sm focus-visible:border-chart-2 focus-visible:ring-chart-2/20" required={nameField.required} />
                      {fieldErrors[nameField.id] && <p className="text-[11px] text-destructive">
                          {fieldErrors[nameField.id].message}
                        </p>}
                    </div>}

                  {/* Entidad u organización */}
                  {orgField && <div className="space-y-1.5">
                      <Label htmlFor={orgField.id} className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                        {orgField.label || "Entidad u organización"}
                        {orgField.required && <span className="text-chart-2 ml-1">*</span>}
                      </Label>
                      <Input id={orgField.id} type="text" placeholder={orgField.placeholder || "Dependencia o Razón Social"} value={formValues[orgField.id] || ""} onChange={e => handleInputChange(orgField.id, e.target.value)} className="bg-background/80 border-border/80 text-foreground placeholder:text-muted-foreground/60 h-10 text-sm focus-visible:border-chart-2 focus-visible:ring-chart-2/20" required={orgField.required} />
                      {fieldErrors[orgField.id] && <p className="text-[11px] text-destructive">
                          {fieldErrors[orgField.id].message}
                        </p>}
                    </div>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Correo electrónico */}
                  {emailField && <div className="space-y-1.5">
                      <Label htmlFor={emailField.id} className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                        {emailField.label || "Correo electrónico"}
                        {emailField.required && <span className="text-chart-2 ml-1">*</span>}
                      </Label>
                      <Input id={emailField.id} type="email" placeholder={emailField.placeholder || "contacto@institucion.gob.mx"} value={formValues[emailField.id] || ""} onChange={e => handleInputChange(emailField.id, e.target.value)} className="bg-background/80 border-border/80 text-foreground placeholder:text-muted-foreground/60 h-10 text-sm focus-visible:border-chart-2 focus-visible:ring-chart-2/20" required={emailField.required} />
                      {fieldErrors[emailField.id] && <p className="text-[11px] text-destructive">
                          {fieldErrors[emailField.id].message}
                        </p>}
                    </div>}

                  {/* Teléfono */}
                  {phoneField && <div className="space-y-1.5">
                      <Label htmlFor={phoneField.id} className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                        {phoneField.label || "Teléfono"}
                        {phoneField.required && <span className="text-chart-2 ml-1">*</span>}
                      </Label>
                      <Input id={phoneField.id} type="tel" placeholder={phoneField.placeholder || "55-0000-0000"} value={formValues[phoneField.id] || ""} onChange={e => handleInputChange(phoneField.id, e.target.value)} className="bg-background/80 border-border/80 text-foreground placeholder:text-muted-foreground/60 h-10 text-sm focus-visible:border-chart-2 focus-visible:ring-chart-2/20" required={phoneField.required} />
                      {fieldErrors[phoneField.id] && <p className="text-[11px] text-destructive">
                          {fieldErrors[phoneField.id].message}
                        </p>}
                    </div>}
                </div>

                {/* Programa de interés */}
                {programField && <div className="space-y-1.5">
                    <Label htmlFor={programField.id} className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                      {programField.label || "Programa de interés"}
                      {programField.required && <span className="text-chart-2 ml-1">*</span>}
                    </Label>
                    {programField.options && programField.options.length > 0 ? <Select value={formValues[programField.id] || ""} onValueChange={val => handleInputChange(programField.id, val)}>
                        <SelectTrigger id={programField.id} className="w-full bg-background/80 border-border/80 text-foreground h-10 text-sm focus-visible:border-chart-2 focus-visible:ring-chart-2/20">
                          <SelectValue placeholder="Seleccione el tema del temario" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover text-popover-foreground border-border">
                          {programField.options.map((opt, i) => <SelectItem key={opt.value} value={opt.value} data-index={i}>
                              {opt.label}
                            </SelectItem>)}
                        </SelectContent>
                      </Select> : <Input id={programField.id} type="text" placeholder={programField.placeholder || "Ej. Armonización CONAC, Cierre Fiscal"} value={formValues[programField.id] || ""} onChange={e => handleInputChange(programField.id, e.target.value)} className="bg-background/80 border-border/80 text-foreground placeholder:text-muted-foreground/60 h-10 text-sm focus-visible:border-chart-2 focus-visible:ring-chart-2/20" />}
                    {fieldErrors[programField.id] && <p className="text-[11px] text-destructive">
                        {fieldErrors[programField.id].message}
                      </p>}
                  </div>}

                {/* Mensaje o requerimientos específicos */}
                {messageField && <div className="space-y-1.5">
                    <Label htmlFor={messageField.id} className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                      {messageField.label || "Mensaje o requerimientos específicos"}
                      {messageField.required && <span className="text-chart-2 ml-1">*</span>}
                    </Label>
                    <Textarea id={messageField.id} rows={3} placeholder={messageField.placeholder || "Indique número estimado de participantes, fechas tentativas o modalidad requerida."} value={formValues[messageField.id] || ""} onChange={e => handleInputChange(messageField.id, e.target.value)} className="bg-background/80 border-border/80 text-foreground placeholder:text-muted-foreground/60 min-h-20 text-sm focus-visible:border-chart-2 focus-visible:ring-chart-2/20" required={messageField.required} />
                    {fieldErrors[messageField.id] && <p className="text-[11px] text-destructive">
                        {fieldErrors[messageField.id].message}
                      </p>}
                  </div>}

                {/* Render any additional fields defined in wvc-forms */}
                {unassignedFields.map((field, i) => <div key={field.id} data-index={i} className="space-y-1.5">
                    <Label htmlFor={field.id} className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                      {field.label}
                      {field.required && <span className="text-chart-2 ml-1">*</span>}
                    </Label>
                    {field.type === "select" && field.options ? <Select value={formValues[field.id] || ""} onValueChange={val => handleInputChange(field.id, val)}>
                        <SelectTrigger id={field.id} className="w-full bg-background/80 border-border/80 text-foreground h-10 text-sm">
                          <SelectValue placeholder="Seleccione una opción" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover text-popover-foreground border-border">
                          {field.options.map((opt, optIndex) => <SelectItem key={opt.value} value={opt.value} data-index={optIndex}>
                              {opt.label}
                            </SelectItem>)}
                        </SelectContent>
                      </Select> : field.type === "textarea" ? <Textarea id={field.id} value={formValues[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} placeholder={field.placeholder} className="bg-background/80 border-border/80 text-foreground placeholder:text-muted-foreground/60 text-sm" required={field.required} /> : <Input id={field.id} type={field.type === "number" ? "number" : "text"} value={formValues[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} placeholder={field.placeholder} className="bg-background/80 border-border/80 text-foreground placeholder:text-muted-foreground/60 h-10 text-sm" required={field.required} />}
                    {fieldErrors[field.id] && <p className="text-[11px] text-destructive">
                        {fieldErrors[field.id].message}
                      </p>}
                  </div>)}

                <div className="pt-2">
                  <Button type="submit" disabled={isSubmitting} className="w-full h-11 bg-primary border border-chart-2 text-primary-foreground font-semibold uppercase tracking-widest text-xs rounded-sm hover:bg-chart-2 hover:text-chart-1 transition-colors duration-200 flex items-center justify-center gap-2 shadow-sm">
                    {isSubmitting ? <>
                        <div className="size-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                        <span>Procesando...</span>
                      </> : <>
                        <Send className="size-4 text-chart-2" />
                        <span>Solicitar información</span>
                      </>}
                  </Button>
                </div>

                <p className="text-[11px] text-center text-muted-foreground pt-1">
                  Tratamiento confidencial de datos bajo estricto secreto profesional.
                </p>
              </form>}
          </motion.div>

        </div>
      </div>
    </section>;
}